import { Control, Controller, FieldErrors, FieldValues, Path, UseFormSetValue } from 'react-hook-form';
import { StanTipTapEditor } from '@/components/core-components/StanTipTapEditor/StanTipTapEditor';
import styles from '@/components/core-components/StanTipTapEditor/StanTipTapEditor.module.css';
import { TemplateVariables } from '../emails/types';

interface EmailBodyFieldProps<T extends FieldValues> {
    control: Control<T>;
    errors: FieldErrors<T>;
    setValue: UseFormSetValue<T>;
    disabled?: boolean;
    templateVariables?: TemplateVariables | null;
    templateDefaults?: { subject: string; body: string } | null;
    onBodyChange?: (body: string) => void;
}

export const EmailBodyField = <T extends FieldValues & { body: string }>({
    control,
    errors,
    setValue,
    disabled = false,
    templateVariables,
    templateDefaults,
    onBodyChange,
}: EmailBodyFieldProps<T>) => {
    return (
        <div className='relative flex flex-col gap-2'>
            <label className='text-label-xs-bold block'>Email Body</label>
            <Controller
                name={'body' as Path<T>}
                control={control}
                render={({ field }) => (
                    <>
                        <StanTipTapEditor
                            value={field.value as string}
                            onChange={value => {
                                field.onChange(value);
                                onBodyChange?.(value);
                            }}
                            error={!!errors.body}
                            disabled={disabled}
                            imageUploadAllowed
                            videoAllowed={false}
                            linkAllowed
                            generateAiAllowed={false}
                            personalizeItemData={
                                templateVariables?.body?.map(v => ({
                                    name: v.name,
                                    id: v.id,
                                    required: v.required,
                                })) || []
                            }
                            allowAddProductLink
                            placeholder='Your email content...'
                        />
                        {templateDefaults?.body && field.value !== templateDefaults.body && (
                            <div className='flex'>
                                <button
                                    type='button'
                                    onClick={() =>
                                        setValue('body' as Path<T>, templateDefaults?.body as any, {
                                            shouldDirty: false,
                                            shouldTouch: true,
                                            shouldValidate: true,
                                        })
                                    }
                                    disabled={disabled}
                                    className={`${styles.externalButton} mt-2 ml-auto`}
                                >
                                    Restore Default
                                </button>
                            </div>
                        )}
                    </>
                )}
            />
            {errors.body && (
                <p className='text-negative-active text-label-xs-bold absolute bottom-4 left-0'>
                    {String(errors.body.message || '')}
                </p>
            )}
        </div>
    );
};
